/**
 * ShareDealButton — one-click share toggle for a Property.
 *
 * Fetches current share state on mount. When public, shows the URL + copy
 * button. When private, offers "Make Public" CTA. Also lists recent buyer
 * inquiries so wholesalers see who's reached out from the pitch page.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Share2, Copy, Eye, Users, Loader2, X, Check, ExternalLink } from "lucide-react";

export default function ShareDealButton({ propertyId }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/v3/properties/${propertyId}/share`);
      setState(data);
    } catch {
      setState({ exists: false });
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const publicUrl = state?.slug
    ? `${window.location.origin}/api/public/preview/deal/${state.slug}`
    : "";

  const toggle = useCallback(async (nextPublic) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/v3/properties/${propertyId}/share`, {
        is_public: nextPublic,
      });
      setState((s) => ({ ...s, ...data, exists: true }));
      toast.success(nextPublic ? "Deal is now public — share the link!" : "Sharing disabled.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update share state.");
    } finally {
      setBusy(false);
    }
  }, [propertyId]);

  const copy = useCallback(() => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl).then(
      () => toast.success("Link copied — paste anywhere."),
      () => toast.error("Copy failed. Long-press the link to copy.")
    );
  }, [publicUrl]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] text-slate-900 hover:bg-slate-100 transition-colors"
        data-testid="share-deal-btn"
      >
        <Share2 size={13} />
        <span>Share</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
          data-testid="share-deal-modal"
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 border-b border-slate-100">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                  Shareable Pitch Page
                </div>
                <div className="mt-1 font-display font-black text-lg text-slate-900">
                  Sell this deal from a link.
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-slate-400 hover:text-slate-700 p-1"
                data-testid="share-deal-close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5">
              {loading ? (
                <div className="flex items-center gap-2 text-slate-500 text-sm">
                  <Loader2 size={14} className="animate-spin" /> Loading share state…
                </div>
              ) : state?.exists && state?.is_public ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm" data-testid="share-status-public">
                    <Check size={14} />
                    <span className="font-semibold">Public link is live</span>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Your shareable URL</div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs font-mono text-slate-900 truncate select-all" data-testid="share-url">
                        {publicUrl}
                      </code>
                      <button
                        onClick={copy}
                        className="inline-flex items-center gap-1 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-2.5 py-1.5 text-xs font-medium transition-colors"
                        data-testid="share-copy-btn"
                      >
                        <Copy size={12} /> Copy
                      </button>
                      <a
                        href={publicUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 hover:bg-slate-100 px-2.5 py-1.5 text-xs font-medium text-slate-700 transition-colors"
                        data-testid="share-open-btn"
                      >
                        <ExternalLink size={12} /> Open
                      </a>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                        <Eye size={11} /> Total views
                      </div>
                      <div className="mt-1 text-2xl font-black text-slate-900 tabular-nums" data-testid="share-view-count">
                        {state.view_count ?? 0}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {state.unique_view_count ?? 0} unique
                      </div>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                        <Users size={11} /> Inquiries
                      </div>
                      <div className="mt-1 text-2xl font-black text-slate-900 tabular-nums" data-testid="share-inquiry-count">
                        {state.inquiry_count ?? 0}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">Cash buyers reached out</div>
                    </div>
                  </div>

                  {state.inquiries?.length > 0 && (
                    <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-56 overflow-y-auto">
                      {state.inquiries.slice(0, 8).map((inq) => (
                        <div key={inq.id || inq.ts} className="p-3 flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">{inq.name}</div>
                            <div className="text-xs text-slate-500">{inq.email} · {inq.phone}</div>
                            {inq.cash_ready && (
                              <div className="mt-0.5 text-[11px] text-emerald-700 font-medium">Cash: {inq.cash_ready}</div>
                            )}
                            {inq.message && (
                              <div className="mt-1 text-xs text-slate-600 line-clamp-2">{inq.message}</div>
                            )}
                          </div>
                          <div className="text-[10px] text-slate-400 shrink-0">
                            {new Date(inq.ts).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => toggle(false)}
                    disabled={busy}
                    className="w-full mt-2 text-xs text-rose-600 hover:text-rose-800 py-2 disabled:opacity-50"
                    data-testid="share-disable-btn"
                  >
                    {busy ? "Working…" : "Disable public link"}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Generate a beautiful, mobile-friendly landing page for this property. Text or email
                    the URL to your cash buyers — they can view the deal, see your MAO/ARV numbers, and
                    submit an offer without logging in.
                  </p>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5" /> Executive design, branded with your logo</li>
                    <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5" /> Live analytics — track views + inquiries</li>
                    <li className="flex gap-2"><Check size={14} className="text-emerald-500 mt-0.5" /> One-click copy for SMS/email blasts</li>
                  </ul>
                  <button
                    onClick={() => toggle(true)}
                    disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 transition-colors disabled:opacity-60"
                    data-testid="share-enable-btn"
                  >
                    {busy ? (
                      <><Loader2 size={14} className="animate-spin" /> Generating link…</>
                    ) : (
                      <><Share2 size={14} /> Make Public → Generate Link</>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
